import React, { useState } from "react";
import CabIcon from "./cabinet/CabIcon";

/**
 * InstallGuide — инструкции по установке приложений Рублокса.
 *
 * Объясняет пользователю, почему ОС показывает предупреждение (SmartScreen на
 * Windows, «неизвестные источники» на Android) и что это НЕ вирус, а штатное
 * поведение для приложений без платного сертификата. Пошаговые инструкции
 * с нарисованными (SVG) макетами окон — без реальных скриншотов.
 */

/* ── Нарисованный макет окна Windows SmartScreen ────────────────────── */
function SmartScreenMock() {
  return (
    <svg viewBox="0 0 320 200" width="100%" style={{ maxWidth: 320, display: "block" }} role="img" aria-label="Окно SmartScreen">
      <rect x="0" y="0" width="320" height="200" rx="8" fill="#c42b1c" />
      {/* крестик */}
      <rect x="284" y="10" width="26" height="20" rx="3" fill="#ffffff" opacity="0.18" />
      <path d="M291 16l12 8M303 16l-12 8" stroke="#fff" strokeWidth="1.5" />
      {/* заголовок */}
      <text x="20" y="42" fill="#fff" fontSize="17" fontWeight="700" fontFamily="system-ui">Система Windows защитила</text>
      <text x="20" y="62" fill="#fff" fontSize="17" fontWeight="700" fontFamily="system-ui">ваш компьютер</text>
      {/* текст */}
      <text x="20" y="92" fill="#fff" fontSize="10" opacity="0.92" fontFamily="system-ui">Фильтр SmartScreen предотвратил запуск</text>
      <text x="20" y="106" fill="#fff" fontSize="10" opacity="0.92" fontFamily="system-ui">неопознанного приложения.</text>
      {/* ссылка «Подробнее» — подсвечена */}
      <text x="20" y="132" fill="#fff" fontSize="11" fontWeight="700" textDecoration="underline" fontFamily="system-ui">Подробнее</text>
      <circle cx="78" cy="128" r="13" fill="none" stroke="#ffe24a" strokeWidth="2.5" />
      <text x="96" y="132" fill="#ffe24a" fontSize="10" fontWeight="700" fontFamily="system-ui">← шаг 1</text>
      {/* кнопка «Выполнить в любом случае» (появляется после клика) */}
      <rect x="20" y="150" width="150" height="30" rx="4" fill="#ffffff" opacity="0.95" />
      <text x="32" y="170" fill="#c42b1c" fontSize="10" fontWeight="700" fontFamily="system-ui">Выполнить в любом случае</text>
      <text x="178" y="170" fill="#ffe24a" fontSize="10" fontWeight="700" fontFamily="system-ui">← шаг 2</text>
    </svg>
  );
}

/* ── Нарисованный макет Android-диалога ─────────────────────────────── */
function AndroidMock() {
  return (
    <svg viewBox="0 0 280 220" width="100%" style={{ maxWidth: 280, display: "block" }} role="img" aria-label="Диалог установки Android">
      <rect x="0" y="0" width="280" height="220" rx="14" fill="#1f232b" stroke="#333a45" />
      {/* иконка приложения */}
      <rect x="118" y="20" width="44" height="44" rx="10" fill="#4f74ff" />
      <path d="M140 30 L150 50 L130 50 Z" fill="#fff" />
      <text x="140" y="84" fill="#e8e8ea" fontSize="13" fontWeight="700" textAnchor="middle" fontFamily="system-ui">Рублокс</text>
      {/* текст */}
      <text x="140" y="112" fill="#9aa0ac" fontSize="10" textAnchor="middle" fontFamily="system-ui">В целях безопасности телефон не может</text>
      <text x="140" y="126" fill="#9aa0ac" fontSize="10" textAnchor="middle" fontFamily="system-ui">устанавливать приложения из этого источника.</text>
      {/* кнопки */}
      <rect x="40" y="150" width="90" height="34" rx="8" fill="#2a2f38" />
      <text x="85" y="172" fill="#9aa0ac" fontSize="11" textAnchor="middle" fontFamily="system-ui">Отмена</text>
      <rect x="150" y="150" width="90" height="34" rx="8" fill="#4f74ff" />
      <text x="195" y="172" fill="#fff" fontSize="11" fontWeight="700" textAnchor="middle" fontFamily="system-ui">Настройки</text>
      <text x="150" y="206" fill="#ffe24a" fontSize="10" fontWeight="700" fontFamily="system-ui">↑ нажмите «Настройки» и разрешите</text>
    </svg>
  );
}

const cardStyle = {
  background: "var(--rb-bg-card, #1a1d24)",
  border: "1px solid var(--rb-border, #2a2f38)",
  borderRadius: 16,
  padding: "22px 24px",
  flex: "1 1 320px",
  minWidth: 280,
};
const stepStyle = {
  display: "flex",
  gap: 12,
  marginBottom: 12,
  alignItems: "flex-start",
};
const numStyle = {
  flexShrink: 0,
  width: 26,
  height: 26,
  borderRadius: "50%",
  background: "#4f74ff",
  color: "#fff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontWeight: 700,
  fontSize: 13,
};

function Step({ n, children }) {
  return (
    <div style={stepStyle}>
      <div style={numStyle}>{n}</div>
      <div style={{ paddingTop: 2, lineHeight: 1.45 }}>{children}</div>
    </div>
  );
}

export default function InstallGuide() {
  const [open, setOpen] = useState(false);

  return (
    <div style={{ marginTop: 40 }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          width: "100%",
          padding: "16px 20px",
          background: "var(--rb-bg-elevated, #20242c)",
          border: "1px solid var(--rb-border, #2a2f38)",
          borderRadius: 12,
          color: "var(--rb-text, #e8e8ea)",
          fontSize: 17,
          fontWeight: 700,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {/* SVG вместо эмодзи: эмодзи выглядит по-разному в каждой ОС,
            не красится в цвет темы и не масштабируется с иконками.
            Шеврон был текстовым «▾» — он наследовал кегль шрифта
            и не выравнивался по центру строки. */}
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8, minWidth: 0 }}>
          <CabIcon name="shield" size={18} />
          Как установить? Почему ОС показывает предупреждение
        </span>
        <CabIcon
          name="arrowRight"
          size={18}
          style={{
            flex: "none",
            transform: open ? "rotate(-90deg)" : "rotate(90deg)",
            transition: "transform .2s",
          }}
        />
      </button>

      {open && (
        <div style={{ marginTop: 18 }}>
          {/* Объяснение — почему вообще предупреждение */}
          <div
            style={{
              background: "rgba(79,116,255,0.10)",
              border: "1px solid rgba(79,116,255,0.30)",
              borderRadius: 12,
              padding: "16px 20px",
              marginBottom: 20,
              color: "var(--rb-text, #e8e8ea)",
              lineHeight: 1.55,
              fontSize: 15,
            }}
          >
            <b>Это не вирус — приложение полностью безопасно.</b> Windows и
            Android показывают предупреждение для <i>любого</i> приложения,
            которое скачано не из официального магазина (Microsoft Store /
            Google Play) и пока не имеет платного сертификата издателя. Это
            штатная защита ОС, а не признак угрозы. Рублокс — это просто окно
            нашего сайта rublox.pro в виде приложения. Ниже — как пройти эти
            окна за пару кликов.
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", gap: 20 }}>
            {/* Windows */}
            <div style={cardStyle}>
              <h3 style={{ margin: "0 0 14px", fontSize: 19, color: "var(--rb-text, #e8e8ea)" }}>
                <CabIcon name="gamepad" size={17} /> Windows
              </h3>
              <p style={{ margin: "0 0 16px", color: "var(--rb-text-faint, #9aa0ac)", fontSize: 14, lineHeight: 1.5 }}>
                После запуска установщика появится <b>синее или красное</b> окно
                «Система Windows защитила ваш компьютер». Не пугайтесь:
              </p>
              <Step n={1}>
                Нажмите ссылку <b>«Подробнее»</b> в окне (она маленькая, под текстом).
              </Step>
              <Step n={2}>
                Появится кнопка <b>«Выполнить в любом случае»</b> — нажмите её.
              </Step>
              <Step n={3}>Установщик запустится. Дальше — обычная установка.</Step>
              <div style={{ marginTop: 16 }}>
                <SmartScreenMock />
              </div>
            </div>

            {/* Android */}
            <div style={cardStyle}>
              <h3 style={{ margin: "0 0 14px", fontSize: 19, color: "var(--rb-text, #e8e8ea)" }}>
                <CabIcon name="robot" size={17} /> Android
              </h3>
              <p style={{ margin: "0 0 16px", color: "var(--rb-text-faint, #9aa0ac)", fontSize: 14, lineHeight: 1.5 }}>
                При открытии скачанного APK телефон попросит разрешение на
                установку из этого источника. Это нормально:
              </p>
              <Step n={1}>Откройте скачанный файл <b>Rublox.apk</b> (из загрузок или браузера).</Step>
              <Step n={2}>
                Если появится «Запрещено в целях безопасности» — нажмите{" "}
                <b>«Настройки»</b> и включите <b>«Разрешить установку из этого
                источника»</b>.
              </Step>
              <Step n={3}>Вернитесь назад и нажмите <b>«Установить»</b>.</Step>
              <div style={{ marginTop: 16 }}>
                <AndroidMock />
              </div>
            </div>
          </div>

          <p style={{ marginTop: 18, color: "var(--rb-text-faint, #9aa0ac)", fontSize: 13, lineHeight: 1.5, textAlign: "center" }}>
            Со временем, когда приложение скачает много пользователей,
            предупреждения перестанут появляться автоматически.
          </p>
        </div>
      )}
    </div>
  );
}
